/**
 * What each person did.
 *
 * Deliberately not a ranking, a score or a colour-coded league table. The
 * busiest cashier will always have the most voids and the most removals; an
 * algorithm that turns that into "risk" produces an accusation the data cannot
 * support, and an owner who trusts it will fire the wrong person.
 *
 * So: counts and money, side by side, sorted by how much each person sold.
 * The judgement is the owner's, and the evidence is one tap away in the flags.
 */

import type { ReactNode } from 'react';
import { EmptyState } from '../../components/ui';
import { money } from './format';
import type { StaffActivity } from './types';

export function StaffTable({
  staff,
  currency,
}: {
  staff: StaffActivity[];
  currency: string;
}): ReactNode {
  if (staff.length === 0) {
    return (
      <EmptyState
        title="Nobody recorded anything"
        description="No sales, voids, stock changes or counts in this period."
      />
    );
  }

  return (
    <div className="space-y-2">
      {staff.map((person) => {
        const variance = Number(person.cashVariance);
        return (
          <div
            key={person.userId ?? person.name}
            className="rounded-xl border border-slate-200 p-3"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="font-medium text-slate-900">{person.name}</p>
              <p className="text-sm tabular-nums text-slate-600">
                {money(person.salesValue, currency)} in sales
              </p>
            </div>

            <dl className="mt-2 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
              <div>
                <dt className="text-slate-500">Sales</dt>
                <dd className="tabular-nums text-slate-900">{person.salesCount}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Voided</dt>
                <dd className="tabular-nums text-slate-900">
                  {person.voidedCount}
                  {person.voidedCount > 0 && (
                    <span className="ml-1 text-slate-500">
                      ({money(person.voidedValue, currency)})
                    </span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">Stock removed</dt>
                <dd className="tabular-nums text-slate-900">
                  {person.stockRemovals}
                  {person.stockRemovals > 0 && (
                    <span className="ml-1 text-slate-500">
                      ({money(person.stockRemovedValue, currency)})
                    </span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">Cash counts</dt>
                <dd className="tabular-nums text-slate-900">
                  {person.cashCounts}
                  {person.cashCounts > 0 && (
                    <span
                      className={`ml-1 ${variance < 0 ? 'text-rose-700' : variance > 0 ? 'text-amber-700' : 'text-emerald-700'}`}
                    >
                      ({variance < 0 ? 'short ' : variance > 0 ? 'over ' : ''}
                      {money(Math.abs(variance), currency)})
                    </span>
                  )}
                </dd>
              </div>
            </dl>
          </div>
        );
      })}
    </div>
  );
}
